import {
  holdRynReplaySecretPagePath,
  scrubRynReplayNetworkUrl,
} from '@ups/xplat/base/ryn-replay';
import {
  canReadRynReplayResponseHeaders,
  createRynReplayTraceparent,
  extractRynReplayOperationId,
  mergeRynReplayTraceparent,
  RYN_REPLAY_TRACEPARENT_HEADER,
  shouldInjectRynReplayTraceparent,
} from './ryn-replay-operation-id';
import { safeSerialize } from './ryn-replay-safe-serialize';
import { readRynReplayXhrBody } from './ryn-replay-xhr-body';

export * from './ryn-replay-devtools-capture.types';
import {
  IRynReplayDevtoolsCapture,
  IRynReplayDevtoolsCaptureOptions,
} from './ryn-replay-devtools-capture.types';

/**
 * Console + network capture for Ryn Replay (#30070).
 *
 * Patches are installed for the life of a recording session and always torn
 * down on stop. Handlers never throw into the host app: a broken serializer
 * must not take down the page the user is using.
 *
 * Console args are fail-closed for privacy (#30073 AC3): only `console.error`
 * is recorded. `log`/`info`/`warn` are left to the browser console only so
 * redacted debug dumps do not flood the replay player. Errors keep the full
 * argument tree with SSN-shaped groups masked and URL fields scrubbed.
 */

import {
  RYN_REPLAY_CONSOLE_EVENT_TYPE,
  RYN_REPLAY_NETWORK_EVENT_TYPE,
} from '@ups/xplat/base/ryn-replay';

export { RYN_REPLAY_CONSOLE_EVENT_TYPE, RYN_REPLAY_NETWORK_EVENT_TYPE };
export * from './ryn-replay-console-scrub';

import {
  RYN_REPLAY_CONSOLE_ARG_MAX_CHARS,
  RYN_REPLAY_NETWORK_BODY_MAX_CHARS,
  scrubConsoleArg,
} from './ryn-replay-console-scrub';

/**
 * A fetch the app cancelled (AbortController) rejects with an `AbortError` (WI 34061).
 * It is not a failure: grids cancel their previous load on every page, sort or search.
 */
export const isRynReplayAbortError = (err: unknown): boolean =>
  (err as { name?: unknown } | null)?.name === 'AbortError';

export class RynReplayDevtoolsCapture implements IRynReplayDevtoolsCapture {
  private installed = false;
  private originalConsole: Partial<
    Record<'log' | 'info' | 'warn' | 'error', (...args: unknown[]) => void>
  > = {};
  private originalFetch?: typeof fetch;
  private OriginalXhr?: typeof XMLHttpRequest;

  constructor(private readonly options: IRynReplayDevtoolsCaptureOptions) {}

  install(): void {
    if (this.installed) {
      return;
    }
    this.installed = true;
    this.patchConsole();
    this.patchFetch();
    this.patchXhr();
  }

  uninstall(): void {
    if (!this.installed) {
      return;
    }
    this.installed = false;
    const consoleRef = this.options.consoleRef ?? globalThis.console;
    for (const level of ['log', 'info', 'warn', 'error'] as const) {
      const original = this.originalConsole[level];
      if (original && consoleRef) {
        consoleRef[level] = original as Console['log'];
      }
    }
    this.originalConsole = {};

    if (this.originalFetch) {
      globalThis.fetch = this.originalFetch;
      this.originalFetch = undefined;
    }

    if (this.OriginalXhr) {
      (
        globalThis as { XMLHttpRequest?: typeof XMLHttpRequest }
      ).XMLHttpRequest = this.OriginalXhr;
      this.OriginalXhr = undefined;
    }
  }

  private patchConsole(): void {
    const consoleRef = this.options.consoleRef ?? globalThis.console;
    if (!consoleRef) {
      return;
    }
    // Only console.error is worth storing: log/info/warn are almost always debug
    // dumps that scrub to useless "[redacted]" rows in the player (#31316).
    for (const level of ['error'] as const) {
      const original = consoleRef[level]?.bind(consoleRef);
      if (!original) {
        continue;
      }
      this.originalConsole[level] = original;
      const errorMode = this.options.consoleErrorMode ?? 'diagnostic';
      consoleRef[level] = (...args: unknown[]) => {
        try {
          this.options.emit(RYN_REPLAY_CONSOLE_EVENT_TYPE, {
            level,
            // Scrub before serialize; diagnostic mode keeps stacks/messages (#31319).
            // An app that opts into maskAllText sets consoleErrorMode: 'privacy'
            // instead, so console.error redacts like log/info/warn (PR 3639 review).
            args: args.map((a) =>
              safeSerialize(
                scrubConsoleArg(a, 0, undefined, errorMode),
                RYN_REPLAY_CONSOLE_ARG_MAX_CHARS
              )
            ),
          });
        } catch {
          // never break console
        }
        return original(...args);
      };
    }
  }

  private patchFetch(): void {
    const fetchRef =
      this.options.fetchRef ?? globalThis.fetch?.bind(globalThis);
    if (!fetchRef) {
      return;
    }
    this.originalFetch = fetchRef;
    globalThis.fetch = async (
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<Response> => {
      const started = Date.now();
      const method = (
        init?.method ??
        (typeof Request !== 'undefined' && input instanceof Request
          ? input.method
          : 'GET')
      ).toUpperCase();
      const rawUrl =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.toString()
            : input.url;
      const url = scrubRynReplayNetworkUrl(rawUrl);
      const inject = shouldInjectRynReplayTraceparent(rawUrl, {
        ingestUrl: this.options.ingestUrl,
        allowedApiBaseUrls: this.options.correlationAllowedApiBaseUrls,
      });
      const minted = inject ? createRynReplayTraceparent() : undefined;
      const merged = minted
        ? mergeRynReplayTraceparent(input, init, minted.traceparent)
        : undefined;
      // Effective id on the wire (existing header wins over minted).
      const stamped = merged
        ? {
            operationId: merged.operationId,
            traceparent: merged.traceparent,
          }
        : undefined;
      try {
        const response = await fetchRef(input, merged?.init ?? init);
        const allowBody = this.options.isPayloadRecordingAllowed(rawUrl);
        let responseBody: unknown;
        // Only parse the body for ProblemDetails.traceId when we did not stamp
        // a correlation id ourselves — otherwise the extract path is unused.
        let bodyText: string | undefined;
        if (allowBody) {
          try {
            const clone = response.clone();
            const text = await clone.text();
            bodyText = text;
            responseBody = safeSerialize(
              text,
              RYN_REPLAY_NETWORK_BODY_MAX_CHARS
            );
          } catch {
            responseBody = undefined;
          }
        }
        this.safeEmitNetwork({
          method,
          url,
          status: response.status,
          durationMs: Date.now() - started,
          ...(responseBody !== undefined ? { responseBody } : {}),
          ...this.operationIdFields(
            stamped?.operationId,
            this.correlationHeaderInputs(rawUrl, stamped?.operationId, {
              requestTraceparent: stamped?.traceparent,
              read: (name) => response.headers.get(name),
              // Skip body parse for extract when we already have a stamped id.
              bodyText: stamped?.operationId ? undefined : bodyText,
            })
          ),
        });
        return response;
      } catch (err) {
        this.safeEmitNetwork({
          method,
          url,
          status: 0,
          durationMs: Date.now() - started,
          ...(isRynReplayAbortError(err) ? { aborted: true } : {}),
          error: safeSerialize(err, RYN_REPLAY_CONSOLE_ARG_MAX_CHARS),
          ...(stamped ? { operationId: stamped.operationId } : {}),
        });
        throw err;
      }
    };
  }

  private patchXhr(): void {
    const Xhr =
      this.options.xhrCtor ??
      (globalThis as { XMLHttpRequest?: typeof XMLHttpRequest }).XMLHttpRequest;
    if (!Xhr) {
      return;
    }
    this.OriginalXhr = Xhr;
    // `Patched` is a `function` because it is invoked with `new`, so it owns its
    // own `this` (the XHR) and cannot see the capture instance. Bind what the
    // closure needs rather than aliasing `this`.
    const captureOptions = this.options;
    const safeEmitNetwork = (payload: Record<string, unknown>) =>
      this.safeEmitNetwork(payload);
    const operationIdFields = (
      ...args: Parameters<RynReplayDevtoolsCapture['operationIdFields']>
    ) => this.operationIdFields(...args);
    const correlationHeaderInputs = (
      ...args: Parameters<RynReplayDevtoolsCapture['correlationHeaderInputs']>
    ) => this.correlationHeaderInputs(...args);
    const Patched = function (this: XMLHttpRequest) {
      const xhr = new Xhr();
      let method = 'GET';
      let rawUrl = '';
      let pagePath = ''; // read at open(): navigation can finish first (#34327)
      let releasePagePath = (): void => undefined;
      let started = 0;

      const open = xhr.open.bind(xhr);
      xhr.open = function (
        m: string,
        url: string | URL,
        async?: boolean,
        username?: string | null,
        password?: string | null
      ) {
        method = (m || 'GET').toUpperCase();
        rawUrl = typeof url === 'string' ? url : url.toString();
        pagePath = globalThis.location?.pathname ?? '';
        // Console errors for this request can log after navigation; hold the
        // page until loadend, which fires after the load/error handlers.
        releasePagePath();
        releasePagePath = holdRynReplaySecretPagePath(pagePath);
        return open(m, url, async !== false, username, password);
      };

      let stampedOperationId: string | undefined;
      let stampedTraceparent: string | undefined;
      // WI 34061: the browser reports a cancelled request as status 0, like a network
      // failure. `abort` fires before `loadend`, so loadend can tell the two apart.
      let aborted = false;
      xhr.addEventListener('abort', () => {
        aborted = true;
      });

      xhr.addEventListener('loadend', () => {
        releasePagePath();
        // #34222: a throw here lands in the host app's global error handler.
        try {
          const { bodyText, responseBody } =
            captureOptions.isPayloadRecordingAllowed(rawUrl)
              ? readRynReplayXhrBody(xhr)
              : {};
          safeEmitNetwork({
            method,
            url: scrubRynReplayNetworkUrl(rawUrl, pagePath),
            status: xhr.status,
            durationMs: started ? Date.now() - started : 0,
            ...(aborted ? { aborted: true } : {}),
            ...(responseBody !== undefined
              ? {
                  responseBody: safeSerialize(
                    responseBody,
                    RYN_REPLAY_NETWORK_BODY_MAX_CHARS
                  ),
                }
              : {}),
            ...operationIdFields(
              stampedOperationId,
              correlationHeaderInputs(rawUrl, stampedOperationId, {
                requestTraceparent: stampedTraceparent,
                read: (name) => headerOf(xhr, name),
                bodyText,
              })
            ),
          });
        } catch {
          // never break the host app's XHR
        }
      });

      const send = xhr.send.bind(xhr);
      xhr.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
        started = Date.now();
        if (
          shouldInjectRynReplayTraceparent(rawUrl, {
            ingestUrl: captureOptions.ingestUrl,
            allowedApiBaseUrls: captureOptions.correlationAllowedApiBaseUrls,
          })
        ) {
          const minted = createRynReplayTraceparent();
          try {
            xhr.setRequestHeader(
              RYN_REPLAY_TRACEPARENT_HEADER,
              minted.traceparent
            );
            stampedOperationId = minted.operationId;
            stampedTraceparent = minted.traceparent;
          } catch {
            // Header already set — do not record the unused minted id; fall
            // through to response-header / ProblemDetails extract instead.
          }
        }
        return send(body);
      };

      return xhr;
    } as unknown as typeof XMLHttpRequest;
    Patched.prototype = Xhr.prototype;
    (globalThis as { XMLHttpRequest: typeof XMLHttpRequest }).XMLHttpRequest =
      Patched;
  }

  private safeEmitNetwork(payload: Record<string, unknown>): void {
    try {
      this.options.emit(RYN_REPLAY_NETWORK_EVENT_TYPE, payload);
    } catch {
      // never break network
    }
  }

  /**
   * Builds correlation header inputs for {@link operationIdFields}.
   *
   * Skips forbidden response-header reads when we already stamped the request
   * (minted id wins anyway) or when the URL is cross-origin (reads only spam
   * "Refused to get unsafe header" in DevTools).
   */
  private correlationHeaderInputs(
    rawUrl: string,
    stampedOperationId: string | undefined,
    options: {
      requestTraceparent?: string | null;
      read: (name: string) => string | null;
      bodyText?: string;
    }
  ): {
    requestTraceparent?: string | null;
    responseTraceparent?: string | null;
    requestId?: string | null;
    bodyText?: string;
  } {
    const base = {
      requestTraceparent: options.requestTraceparent,
      bodyText: options.bodyText,
    };
    if (
      stampedOperationId ||
      !canReadRynReplayResponseHeaders(rawUrl, {
        pageOrigin:
          typeof globalThis.location?.origin === 'string'
            ? globalThis.location.origin
            : undefined,
      })
    ) {
      return base;
    }
    return {
      ...base,
      responseTraceparent: options.read(RYN_REPLAY_TRACEPARENT_HEADER),
      requestId: options.read('request-id') ?? options.read('Request-Id'),
    };
  }

  private operationIdFields(
    minted: string | undefined,
    headers: {
      requestTraceparent?: string | null;
      responseTraceparent?: string | null;
      requestId?: string | null;
      bodyText?: string;
    }
  ): { operationId: string } | Record<string, never> {
    const operationId =
      minted ??
      extractRynReplayOperationId({
        requestTraceparent: headers.requestTraceparent,
        responseTraceparent: headers.responseTraceparent,
        requestId: headers.requestId,
        bodyText: headers.bodyText,
      });
    return operationId ? { operationId } : {};
  }
}

const headerOf = (xhr: XMLHttpRequest, name: string): string | null => {
  try {
    return xhr.getResponseHeader(name);
  } catch {
    return null;
  }
};
